'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@common/components';
import { FOCUS_RING } from '@common/components/interaction';
import SvgChevronright from '@common/icon/SvgChevronright';
import { cn, CustomError } from '@core/utils';
import { useQueryClient, useQueryErrorResetBoundary, useSuspenseQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { motion, useReducedMotion } from 'framer-motion';
import { Suspense, useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { AuthQueryKey, AuthQueryOptions } from '@/api';
import type { GiftTrialCampaign } from '@/api/getGiftTrialCampaign';
import { CustomLink } from '@/app/_components/CustomLink';
import { createMarketingConsentDialog } from '@/app/_components/MarketingConsentSheet';
import { DONATION_ROUTES, ROUTES } from '@/constants';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { useAcceptMarketingConsent } from '@/hooks/useAcceptMarketingConsent';
import { useCheckWelloApp } from '@/hooks/useCheckWelloApp';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { usePublishElementHeight } from '@/hooks/usePublishElementHeight';
import { useAuthStore } from '@/stores/AuthStore';
import { closeResponsiveDialog, openResponsiveDialog, tryOpenResponsiveDialog } from '@/stores/ResponsiveDialogStore';
import { createAppOnlyDialog } from '../../_components/AppOnlyDialog';
import { formatSelectionAnnounceDateTime } from '../../_utils/formatCampaignDate';
import { retryUnlessReturnCode } from '../../_utils/retryUnlessReturnCode';
import { IS_WEB_APPLY_ALLOWED } from '../../_utils/webApply';
import type { ApplyStatusResult, CampaignCta as CampaignCtaContent, CampaignCtaBand } from '../_utils/resolveCampaignCta';
import { resolveCampaignCta } from '../_utils/resolveCampaignCta';
import type { GateDialog } from '../_utils/resolveGateDialogList';
import { resolveGateDialogList } from '../_utils/resolveGateDialogList';
import { GateDialogModal } from './GateDialogModal';
const NOT_WELLO_CHANNEL_CODE = 30403;
const MAX_TIMEOUT_MS = 2_147_483_647;
//* 서버가 오픈 시각에 단계를 바꾸는 시차를 기다린다
const OPEN_REFRESH_DELAY_MS = 3_000;
type CtaCampaign = Pick<GiftTrialCampaign, 'campaignId' | 'campaignPhase' | 'applyStartAt' | 'selectionAnnounceDate'>;
type CtaVariant = 'footer' | 'inline';
interface CampaignCtaProps {
  campaign: CtaCampaign;
  recruitDayCount: number;
  //* footer는 데스크톱에서도 숨겨진 채 마운트돼 진입 안내를 한 번만 띄우는 주인이 된다
  variant: CtaVariant;
  isServerLogin: boolean;
  isServerWelloApp: boolean;
}

//* 버튼 시트의 둥근 모서리 뒤로 띠 색이 이어져야 해서 바깥 배경을 띠에 맞춘다
const FOOTER_BAND_BACKGROUND: Record<CampaignCtaBand, string> = {
  'recruit-dday': 'bg-O-50',
  announcement: 'bg-Gray-white',
  donation: 'bg-Gray-black'
};
export const CampaignCta = t0 => {
  const $ = _c(53);
  const {
    campaign,
    recruitDayCount,
    variant,
    isServerLogin,
    isServerWelloApp
  } = t0;
  const containerRef = useRef(null);
  const isLogin = useAuthStore(_temp) ?? isServerLogin;
  const {
    isChecking,
    isWelloApp: isWelloAppFromStore
  } = useCheckWelloApp();
  const isWelloApp = IS_WEB_APPLY_ALLOWED || (isChecking ? isServerWelloApp : isWelloAppFromStore);
  const isDesktop = useMediaQuery(BREAKPOINTS.DESKTOP, false);
  const hasOpenedAppOnlyDialogRef = useRef(false);
  const {
    reset
  } = useQueryErrorResetBoundary();
  const router = useCustomRouter();
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== campaign || $[1] !== isServerLogin || $[2] !== isServerWelloApp || $[3] !== recruitDayCount || $[4] !== variant) {
    t1 = {
      campaign,
      recruitDayCount,
      variant,
      isServerLogin,
      isServerWelloApp
    };
    $[0] = campaign;
    $[1] = isServerLogin;
    $[2] = isServerWelloApp;
    $[3] = recruitDayCount;
    $[4] = variant;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const resolvedProps = t1;
  const t2 = variant === "footer";
  let t3;
  if ($[6] !== t2) {
    t3 = {
      enabled: t2
    };
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  usePublishElementHeight(containerRef, "--bottom-nav-portal-height", t3);
  let t4;
  let t5;
  if ($[8] !== isChecking || $[9] !== isDesktop || $[10] !== isWelloAppFromStore || $[11] !== variant) {
    t4 = () => {
      if (IS_WEB_APPLY_ALLOWED || variant !== "footer" || isChecking || isWelloAppFromStore || !isDesktop || hasOpenedAppOnlyDialogRef.current) {
        return;
      }
      hasOpenedAppOnlyDialogRef.current = tryOpenResponsiveDialog(createAppOnlyDialog());
    };
    t5 = [variant, isChecking, isWelloAppFromStore, isDesktop];
    $[8] = isChecking;
    $[9] = isDesktop;
    $[10] = isWelloAppFromStore;
    $[11] = variant;
    $[12] = t4;
    $[13] = t5;
  } else {
    t4 = $[12];
    t5 = $[13];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[14] !== campaign.applyStartAt || $[15] !== campaign.campaignId || $[16] !== campaign.campaignPhase || $[17] !== queryClient || $[18] !== router || $[19] !== variant) {
    t6 = () => {
      const msUntilOpen = dayjs(campaign.applyStartAt).diff(dayjs());
      if (variant !== "footer" || campaign.campaignPhase !== "UPCOMING" || msUntilOpen <= 0 || msUntilOpen > MAX_TIMEOUT_MS) {
        return;
      }
      const timer = setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: AuthQueryKey["get:/wello/v2/auth/gift-trial/campaigns/{campaignId}/apply-status"]({
            path: {
              campaignId: campaign.campaignId
            }
          })
        });
        router.refresh();
      }, msUntilOpen + OPEN_REFRESH_DELAY_MS);
      return () => {
        clearTimeout(timer);
      };
    };
    t7 = [variant, campaign.applyStartAt, campaign.campaignPhase, campaign.campaignId, queryClient, router];
    $[14] = campaign.applyStartAt;
    $[15] = campaign.campaignId;
    $[16] = campaign.campaignPhase;
    $[17] = queryClient;
    $[18] = router;
    $[19] = variant;
    $[20] = t6;
    $[21] = t7;
  } else {
    t6 = $[20];
    t7 = $[21];
  }
  useEffect(t6, t7);
  let t8;
  bb0: {
    if (!isWelloApp || !isLogin) {
      let t9;
      if ($[22] !== campaign || $[23] !== isLogin) {
        t9 = resolveCampaignCta({
          isLogin,
          campaign
        });
        $[22] = campaign;
        $[23] = isLogin;
        $[24] = t9;
      } else {
        t9 = $[24];
      }
      let t10;
      if ($[25] !== isWelloApp || $[26] !== resolvedProps || $[27] !== t9) {
        t10 = <ResolvedCta {...resolvedProps} isWelloApp={isWelloApp} cta={t9} />;
        $[25] = isWelloApp;
        $[26] = resolvedProps;
        $[27] = t9;
        $[28] = t10;
      } else {
        t10 = $[28];
      }
      t8 = t10;
      break bb0;
    }
    let t9;
    if ($[29] !== campaign || $[30] !== isLogin || $[31] !== resolvedProps) {
      t9 = t10 => {
        const {
          error,
          resetErrorBoundary
        } = t10;
        return error instanceof CustomError && error.return_code === NOT_WELLO_CHANNEL_CODE ? <ResolvedCta {...resolvedProps} isWelloApp={false} cta={resolveCampaignCta({
          isLogin,
          campaign
        })} /> : <div className="bg-Gray-white px-20 py-12"><Button className="w-full" onClick={resetErrorBoundary}>자격 확인에 실패했어요. 다시 시도해 주세요</Button></div>;
      };
      $[29] = campaign;
      $[30] = isLogin;
      $[31] = resolvedProps;
      $[32] = t9;
    } else {
      t9 = $[32];
    }
    let t10;
    if ($[33] !== campaign || $[34] !== isLogin || $[35] !== variant) {
      t10 = variant === "footer" && resolveCampaignCta({
        isLogin,
        campaign
      }).band === "recruit-dday";
      $[33] = campaign;
      $[34] = isLogin;
      $[35] = variant;
      $[36] = t10;
    } else {
      t10 = $[36];
    }
    let t11;
    if ($[37] !== t10 || $[38] !== variant) {
      t11 = <CtaFallback variant={variant} hasBand={t10} />;
      $[37] = t10;
      $[38] = variant;
      $[39] = t11;
    } else {
      t11 = $[39];
    }
    let t12;
    if ($[40] !== resolvedProps) {
      t12 = <MemberCta {...resolvedProps} />;
      $[40] = resolvedProps;
      $[41] = t12;
    } else {
      t12 = $[41];
    }
    let t13;
    if ($[42] !== t11 || $[43] !== t12) {
      t13 = <Suspense fallback={t11}>{t12}</Suspense>;
      $[42] = t11;
      $[43] = t12;
      $[44] = t13;
    } else {
      t13 = $[44];
    }
    let t14;
    if ($[45] !== reset || $[46] !== t13 || $[47] !== t9) {
      t14 = <ErrorBoundary onReset={reset} fallbackRender={t9}>{t13}</ErrorBoundary>;
      $[45] = reset;
      $[46] = t13;
      $[47] = t9;
      $[48] = t14;
    } else {
      t14 = $[48];
    }
    t8 = t14;
  }
  const content = t8;
  if (variant === "inline") {
    let t9;
    if ($[49] !== content) {
      t9 = <div className="flex flex-col overflow-hidden">{content}</div>;
      $[49] = content;
      $[50] = t9;
    } else {
      t9 = $[50];
    }
    return t9;
  }
  let t9;
  if ($[51] !== content) {
    t9 = <div ref={containerRef} className="fixed inset-x-0 bottom-0 z-10 hidden overflow-hidden break-keep rounded-t-medium shadow-md-reverse mobile-tablet:block">{content}</div>;
    $[51] = content;
    $[52] = t9;
  } else {
    t9 = $[52];
  }
  return t9;
};
const CtaFallback = t0 => {
  const $ = _c(14);
  const {
    variant,
    hasBand: t1
  } = t0;
  const hasBand = t1 === undefined ? false : t1;
  const t2 = hasBand && FOOTER_BAND_BACKGROUND["recruit-dday"];
  let t3;
  if ($[0] !== t2) {
    t3 = cn(t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== hasBand) {
    t4 = hasBand ? <p aria-hidden={true} className="invisible px-16 py-12 typo-body3"> </p> : null;
    $[2] = hasBand;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = variant === "footer" && "px-20 py-12";
  const t6 = hasBand && "rounded-t-medium";
  let t7;
  if ($[4] !== t5 || $[5] !== t6) {
    t7 = cn("bg-Gray-white", t5, t6);
    $[4] = t5;
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Button className="w-full" disabled={true}><span className="invisible">체험단 신청하기</span></Button>;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== t7) {
    t9 = <div className={t7}>{t8}</div>;
    $[8] = t7;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== t3 || $[11] !== t4 || $[12] !== t9) {
    t10 = <div className={t3}>{t4}{t9}</div>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  return t10;
};
const MemberCta = props => {
  const $ = _c(16);
  let t0;
  if ($[0] !== props.campaign.campaignId) {
    t0 = AuthQueryOptions["get:/wello/v2/auth/gift-trial/campaigns/{campaignId}/apply-status"]({
      path: {
        campaignId: props.campaign.campaignId
      }
    });
    $[0] = props.campaign.campaignId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = {
      ...t0,
      select: _temp2,
      retry: retryUnlessReturnCode
    };
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    data: applyStatus
  } = useSuspenseQuery(t1);
  if (applyStatus?.applyStatus === "UPCOMING") {
    let t2;
    if ($[4] !== applyStatus || $[5] !== props) {
      t2 = <UpcomingMemberCta {...props} applyStatus={applyStatus} />;
      $[4] = applyStatus;
      $[5] = props;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    return t2;
  }
  const t2 = applyStatus?.applyStatus;
  let t3;
  if ($[7] !== props.campaign || $[8] !== t2) {
    t3 = resolveCampaignCta({
      isLogin: true,
      campaign: props.campaign,
      applyStatus: t2
    });
    $[7] = props.campaign;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== applyStatus) {
    t4 = applyStatus ? resolveGateDialogList(applyStatus) : [];
    $[10] = applyStatus;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== props || $[13] !== t3 || $[14] !== t4) {
    t5 = <ResolvedCta {...props} isWelloApp={true} cta={t3} dialogList={t4} />;
    $[12] = props;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
};
const UpcomingMemberCta = t0 => {
  const $ = _c(8);
  const {
    applyStatus,
    ...props
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      ...AuthQueryOptions["get:/wello/v2/auth/gift-trial/me"](),
      select: _temp3,
      retry: retryUnlessReturnCode
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    data: isDonationEligible
  } = useSuspenseQuery(t1);
  const T0 = ResolvedCta;
  const t2 = true;
  const t3 = resolveCampaignCta({
    isLogin: true,
    campaign: props.campaign,
    applyStatus: applyStatus.applyStatus,
    isDonationEligible
  });
  let t4;
  if ($[1] !== applyStatus) {
    t4 = resolveGateDialogList(applyStatus);
    $[1] = applyStatus;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== T0 || $[4] !== props || $[5] !== t3 || $[6] !== t4) {
    t5 = <T0 {...props} isWelloApp={t2} cta={t3} dialogList={t4} />;
    $[3] = T0;
    $[4] = props;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
};
const ResolvedCta = t0 => {
  const $ = _c(67);
  const {
    campaign,
    recruitDayCount,
    variant,
    isWelloApp,
    cta,
    dialogList: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const dialogList = t2;
  const router = useCustomRouter();
  const queryClient = useQueryClient();
  const acceptConsent = useAcceptMarketingConsent();
  const isReducedMotion = useReducedMotion();
  const t3 = variant === "footer";
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== t3) {
    t5 = {
      isActive: t3,
      dismissedTypeList: t4
    };
    $[3] = t3;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const [gateDialogState, setGateDialogState] = useState(t5);
  let t6;
  if ($[5] !== dialogList || $[6] !== gateDialogState.dismissedTypeList || $[7] !== gateDialogState.isActive) {
    t6 = gateDialogState.isActive ? dialogList.filter(dialog => !gateDialogState.dismissedTypeList.includes(dialog.type)) : [];
    $[5] = dialogList;
    $[6] = gateDialogState.dismissedTypeList;
    $[7] = gateDialogState.isActive;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const dialogQueue = t6;
  const {
    campaignId
  } = campaign;
  let t7;
  if ($[9] !== acceptConsent || $[10] !== campaignId || $[11] !== cta.kind || $[12] !== dialogList || $[13] !== isWelloApp || $[14] !== queryClient || $[15] !== router) {
    t7 = () => {
      if (!isWelloApp) {
        openResponsiveDialog(createAppOnlyDialog());
        return;
      }
      if (dialogList.length > 0) {
        setGateDialogState({
          isActive: true,
          dismissedTypeList: []
        });
        return;
      }
      switch (cta.kind) {
        case "login-required":
          {
            router.push(ROUTES.LOGIN.withSearchParams({
              searchParams: {
                redirect: DONATION_ROUTES.GIFT_TRIAL_DETAIL.pathname({
                  campaignId
                })
              }
            }));
            return;
          }
        case "open":
          {
            router.push(DONATION_ROUTES.GIFT_TRIAL_APPLY.withSearchParams({
              searchParams: {
                campaignId
              }
            }));
            return;
          }
        case "marketing-consent-required":
          {
            openResponsiveDialog(createMarketingConsentDialog({
              title: "\uCCB4\uD5D8\uB2E8\uC5D0 \uCC38\uC5EC\uD558\uB824\uBA74 \uB3D9\uC758\uAC00 \uD544\uC694\uD574\uC694!",
              agreeLabel: "\uB3D9\uC758\uD558\uACE0 \uC2DC\uC791\uD558\uAE30",
              isCloseButtonHidden: true,
              onAgree: () => acceptConsent.mutateAsync().then(() => {
                closeResponsiveDialog();
                return Promise.all([queryClient.invalidateQueries({
                  queryKey: AuthQueryKey["get:/wello/v2/auth/gift-trial/campaigns/{campaignId}/apply-status"]({
                    path: {
                      campaignId
                    }
                  })
                }), queryClient.invalidateQueries({
                  queryKey: AuthQueryKey["get:/wello/v2/auth/gift-trial/me"]()
                })]);
              })
            }));
            return;
          }
        default:
          {
            return;
          }
      }
    };
    $[9] = acceptConsent;
    $[10] = campaignId;
    $[11] = cta.kind;
    $[12] = dialogList;
    $[13] = isWelloApp;
    $[14] = queryClient;
    $[15] = router;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const handleClick = t7;
  let t8;
  bb0: switch (cta.band) {
    case "recruit-dday":
      {
        let t9;
        if ($[17] !== recruitDayCount || $[18] !== variant) {
          t9 = variant === "footer" ? <p className="flex items-center justify-center gap-4 px-16 py-12 font-500 text-Gray-black typo-body4">{recruitDayCount > 0 ? <>모집 종료까지<strong className="font-700 text-O-400 typo-body3">{recruitDayCount}일</strong>남았어요.</> : <>모집이<strong className="font-700 text-O-400 typo-body3">오늘</strong>마감돼요.</>}</p> : null;
          $[17] = recruitDayCount;
          $[18] = variant;
          $[19] = t9;
        } else {
          t9 = $[19];
        }
        t8 = t9;
        break bb0;
      }
    case "announcement":
      {
        const t9 = variant === "footer" ? "gap-8 px-20 pt-12" : "gap-12";
        let t10;
        if ($[20] !== t9) {
          t10 = cn("flex flex-col bg-Gray-white", t9);
          $[20] = t9;
          $[21] = t10;
        } else {
          t10 = $[21];
        }
        let t11;
        if ($[22] !== isReducedMotion) {
          t11 = isReducedMotion ? undefined : {
            x: ["0%", "-66.667%", "0%"]
          };
          $[22] = isReducedMotion;
          $[23] = t11;
        } else {
          t11 = $[23];
        }
        let t12;
        if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
          t12 = {
            duration: 3,
            ease: "easeInOut",
            repeat: Infinity
          };
          $[24] = t12;
        } else {
          t12 = $[24];
        }
        let t13;
        if ($[25] !== t11) {
          t13 = <div className="h-6 overflow-hidden rounded-pill"><motion.div className="h-full w-[300%] bg-gradient-to-r from-Gray-white via-O-200 via-25% to-Gray-white" animate={t11} transition={t12} /></div>;
          $[25] = t11;
          $[26] = t13;
        } else {
          t13 = $[26];
        }
        let t14;
        if ($[27] !== campaign.selectionAnnounceDate) {
          t14 = formatSelectionAnnounceDateTime(campaign.selectionAnnounceDate, "M\uC6D4 D\uC77C");
          $[27] = campaign.selectionAnnounceDate;
          $[28] = t14;
        } else {
          t14 = $[28];
        }
        let t15;
        if ($[29] !== t14) {
          t15 = <p className="font-600">{t14}{" "}발표</p>;
          $[29] = t14;
          $[30] = t15;
        } else {
          t15 = $[30];
        }
        let t16;
        if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
          t16 = cn("group flex items-center gap-4 rounded-xsmall underline-offset-2 transition-colors hover:text-Gray-900 hover:underline active:text-Gray-black active:underline", FOCUS_RING);
          $[31] = t16;
        } else {
          t16 = $[31];
        }
        let t17;
        if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
          t17 = <CustomLink href={DONATION_ROUTES.GIFT_TRIAL_MY.pathname} className={t16}>신청 현황 보기<SvgChevronright aria-hidden={true} className="size-12 transition-transform duration-150 group-hover:translate-x-1 group-focus-visible:translate-x-1 motion-reduce:transition-none" /></CustomLink>;
          $[32] = t17;
        } else {
          t17 = $[32];
        }
        let t18;
        if ($[33] !== t15) {
          t18 = <div className="flex items-center justify-between gap-8 text-Gray-700 typo-body4">{t15}{t17}</div>;
          $[33] = t15;
          $[34] = t18;
        } else {
          t18 = $[34];
        }
        let t19;
        if ($[35] !== t10 || $[36] !== t13 || $[37] !== t18) {
          t19 = <div className={t10}>{t13}{t18}</div>;
          $[35] = t10;
          $[36] = t13;
          $[37] = t18;
          $[38] = t19;
        } else {
          t19 = $[38];
        }
        t8 = t19;
        break bb0;
      }
    case "donation":
      {
        let t9;
        if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
          t9 = cn("group flex items-center justify-between bg-Gray-black px-16 py-12 font-500 text-Gray-25 transition-colors typo-body4 hover:bg-Gray-900 active:bg-Gray-800", FOCUS_RING);
          $[39] = t9;
        } else {
          t9 = $[39];
        }
        let t10;
        if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
          t10 = <CustomLink href={DONATION_ROUTES.GIFTS.pathname} className={t9}>기부하러 가기<SvgChevronright aria-hidden={true} className="size-16 transition-transform duration-150 group-hover:translate-x-1 group-focus-visible:translate-x-1 motion-reduce:transition-none" /></CustomLink>;
          $[40] = t10;
        } else {
          t10 = $[40];
        }
        t8 = t10;
        break bb0;
      }
    default:
      {
        t8 = null;
      }
  }
  const band = t8;
  const t9 = variant === "footer" && cta.band && FOOTER_BAND_BACKGROUND[cta.band];
  let t10;
  if ($[41] !== t9) {
    t10 = cn(t9);
    $[41] = t9;
    $[42] = t10;
  } else {
    t10 = $[42];
  }
  const t11 = variant === "footer" && "rounded-t-medium px-20 py-12";
  const t12 = variant === "inline" && band && "pt-12";
  let t13;
  if ($[43] !== t11 || $[44] !== t12) {
    t13 = cn("bg-Gray-white", t11, t12);
    $[43] = t11;
    $[44] = t12;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  let t14;
  if ($[46] !== cta.disabled || $[47] !== cta.label || $[48] !== handleClick) {
    t14 = <Button className="w-full" disabled={cta.disabled} onClick={handleClick}>{cta.label}</Button>;
    $[46] = cta.disabled;
    $[47] = cta.label;
    $[48] = handleClick;
    $[49] = t14;
  } else {
    t14 = $[49];
  }
  let t15;
  if ($[50] !== t13 || $[51] !== t14) {
    t15 = <div className={t13}>{t14}</div>;
    $[50] = t13;
    $[51] = t14;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  let t16;
  if ($[53] !== band || $[54] !== t10 || $[55] !== t15) {
    t16 = <div className={t10}>{band}{t15}</div>;
    $[53] = band;
    $[54] = t10;
    $[55] = t15;
    $[56] = t16;
  } else {
    t16 = $[56];
  }
  let t17;
  if ($[57] !== dialogQueue[0]) {
    t17 = () => setGateDialogState(state => ({
      ...state,
      dismissedTypeList: dialogQueue[0] ? [...state.dismissedTypeList, dialogQueue[0].type] : state.dismissedTypeList
    }));
    $[57] = dialogQueue[0];
    $[58] = t17;
  } else {
    t17 = $[58];
  }
  let t18;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => setGateDialogState({
      isActive: false,
      dismissedTypeList: []
    });
    $[59] = t18;
  } else {
    t18 = $[59];
  }
  let t19;
  if ($[60] !== campaignId || $[61] !== dialogQueue[0] || $[62] !== t17) {
    t19 = <GateDialogModal campaignId={campaignId} dialog={dialogQueue[0]} onClose={t17} onLeave={t18} />;
    $[60] = campaignId;
    $[61] = dialogQueue[0];
    $[62] = t17;
    $[63] = t19;
  } else {
    t19 = $[63];
  }
  let t20;
  if ($[64] !== t16 || $[65] !== t19) {
    t20 = <>{t16}{t19}</>;
    $[64] = t16;
    $[65] = t19;
    $[66] = t20;
  } else {
    t20 = $[66];
  }
  return t20;
};
function _temp(state) {
  return state.isLogin;
}
function _temp2(t0) {
  const {
    context
  } = t0;
  return context;
}
function _temp3(t0) {
  const {
    context
  } = t0;
  return context?.profile.donationEligible;
}